import { ref, computed } from 'vue'

const currentLocale = ref('en')

const messages = {
  en: {
    nav: { home: 'Home', products: 'Products', productList: 'Product List', cases: 'Cases', about: 'About Us', contact: 'Contact', news: 'News' },
    news: {
      title: 'News & Insights',
      subtitle: 'Stay updated with the latest developments in portable energy technology and industry trends.',
      readMore: 'Read More',
      mostVisited: 'Recent News',
      noArticles: 'No articles in this category.',
      all: 'All',
    },
    newsDetail: {
      back: 'Back to News',
      previous: 'Previous Article',
      next: 'Next Article',
    },
    lang: { switch: '中文', current: 'English' },
    home: {
      hero: {
        title: 'Power Your Adventure',
        subtitle: 'High-capacity portable power stations for outdoor, emergency, and commercial use.',
        ctaQuote: 'Request a Quote',
        ctaCatalog: 'Explore Products',
        ctaVideo: 'Watch Video',
      },
      trust: {
              label: 'Trusted by 500+ Companies Worldwide',
            },
      products: { title: 'Featured Products', subtitle: 'Standard models and custom solutions for business buyers', viewAll: 'View All Products' },
      about: {
        label: 'About Coronet',
        title: 'Powering the World, One Station at a Time',
        desc: 'Coronet is a professional manufacturer of outdoor portable power stations and power banks. Since 2020, we have helped hundreds of B2B partners worldwide leverage portable energy solutions to empower their businesses and customers.',
        cta: 'Learn More About Us',
      },
      advantages: {
        title: 'Why Choose Us',
        items: [
          { title: 'High Capacity', desc: 'Up to 50,000mAh for extended outdoor use' },
          { title: 'Fast Charging', desc: 'Latest fast charge technology up to 65W' },
          { title: 'Global Delivery', desc: 'Shipping to 50+ countries with reliable logistics' },
          { title: 'Custom OEM', desc: 'Custom branding and design support for B2B partners' },
        ],
      },
      scenarios: {
        title: 'Power Anywhere, Anytime',
        subtitle: 'From camping trips to emergency backup, our products deliver reliable power wherever you need it.',
      },
      testimonials: {
        title: 'What Our Clients Say',
      },
      stats: {
        title: 'Our Impact in Numbers',
      },
      news: { title: 'Latest News', viewAll: 'View All News' },
      cta: {
        title: 'Ready to Power Your Business?',
        desc: 'Contact us today for a custom quote or to learn more about our OEM/ODM solutions.',
        btnContact: 'Contact Us',
        btnQuote: 'Request a Quote',
      },
    },
    products: {
      title: 'Power Bank Product Catalog',
      subtitle: 'Standard models and custom solutions for business buyers.',
      categories: { all: 'All Products', portableStorage: 'Portable Storage Power', residentialStorage: 'Residential Storage System', commercialStorage: 'Commercial Storage System', emergencyPower: 'Multi-functional Emergency Power' },
      viewCase: 'View Details',
      heroLabel: 'Outdoor Power Stations',
      heroTitle: 'Portable Power for Every Adventure',
      heroSubtitle: 'High-capacity portable power stations for outdoor, emergency, and commercial use.',
      heroCta: 'Request a Quote',
      heroCta2: 'Explore Products',
      introLabel: 'Outdoor Power Stations',
      introTitle: 'Each Power Station Allows You to Generate Your Own Electricity',
      introDesc: 'Our portable power stations combine high-capacity batteries with multiple output ports, enabling you to access reliable power anywhere. From camping trips to emergency backup, our products deliver consistent performance.',
      introStat: 'Countries with active distribution partners',
      introCta: 'Product List',
      featureLabel: 'Key Features',
      featureTitle: 'We Store Energy Efficiently for Your Home',
      featureCta: 'Contact Us',
      bannerTitle: 'With a Portable Power Station and Smart Energy, Save Up to 60% on Your Electricity Bill',
      bannerDesc: 'Our solutions help businesses and consumers reduce energy costs while maintaining reliable power supply.',
      chartTitle: 'Energy Savings Over Time',
      chartSubtitle: 'Average monthly savings percentage with our power stations',
      cardsCta: 'Get a Quote',
      splitTitle: 'Sell the Energy You Don\'t Use',
      splitDesc: 'Our advanced portable power stations allow businesses to store excess energy and use it when needed, creating additional revenue streams and reducing dependency on the grid.',
      splitCta: 'Learn More',
      newsTitle: 'News and Publications',
      newsViewAll: 'View All',
      moreProducts: 'More Products',
    },
    productList: {
      heroTitle: 'Product Catalog',
      heroSubtitle: 'Portable Power Banks & Outdoor Energy Storage Solutions',
      heroCta: 'Request A Custom Quote',
      viewDetails: 'View Details',
      features: {
        highCapacity: 'High Capacity',
        fastCharging: 'Fast Charging',
        outdoorReady: 'Outdoor Ready',
        customOem: 'Custom OEM',
      },
    },
    productDetail: {
      subtitle: '5000mAh High-Capacity Outdoor Energy Storage',
      output: 'Output',
      compatible: 'Compatible',
      requestQuote: 'Request A Quote',
      capacity: 'Capacity',
      acOutput: 'AC Output',
      dcOutput: 'DC Output',
      chargingTime: 'Charging Time',
      productFeatures: 'Product Features',
      techSpecs: 'Technical Specifications',
      parameter: 'Parameter',
      specification: 'Specification',
      customize: 'Customize This Product',
      yourName: 'Your Name',
      companyName: 'Company Name',
      techSpecLabels: {
        batteryType: 'Battery Type',
        outputPower: 'Output Power',
        capacity: 'Capacity',
        chargingMethod: 'Charging Method',
        operatingTemp: 'Operating Temperature',
        weight: 'Weight',
      },
      quantity: 'Quantity',
      email: 'Email',
      requirements: 'Product Requirements',
      sendInquiry: 'Send Inquiry',
      inquirySent: 'Inquiry sent! We will contact you soon.',
      batteryType: 'Battery Type',
      outputPower: 'Output Power',
      chargingMethod: 'Charging Method',
      operatingTemp: 'Operating Temperature',
      weight: 'Weight',
      callUs: 'Call Us',
      emailUs: 'Email Us',
      relatedProducts: 'Related Products',
      viewDetails: 'View Details',
    },
    cases: {
      title: 'Global Power Bank Projects',
      subtitle: 'Reliable portable energy solutions for retail, industry, events and distribution.',
      regions: { europe: 'Europe', northAmerica: 'North America', asiaPacific: 'Asia Pacific' },
      viewCase: 'View Case',
      trusted: 'Trusted supplier for custom power bank orders.',
    },
    caseDetail: {
      back: 'Back to Cases',
      related: 'Related Cases',
      viewDetails: 'View Details',
      previous: 'Previous Case',
      next: 'Next Case',
      projectInfo: 'Project Info',
      regions: 'Regions',
      solutions: 'Solutions',
      outdoorStorage: 'Outdoor Energy Storage System',
      application: 'Application',
      scenarios: 'Camping, Emergency, Industrial',
      contactDesc: 'Learn more about this case study or get a customized solution.',
    },
    about: {
      title: 'About Our Power Bank Factory',
      subtitle: 'We design and manufacture portable power banks, fast chargers and energy storage solutions for global B2B partners.',
      heroSubtitle: 'Outdoor Power Solutions for Global B2B Partners',
      introLabel: 'Outdoor Power Solutions',
      introTitle: 'We Are Experts in Portable Power & Energy Storage',
      introDesc: 'Hefei Coronet Energy Co., Ltd. was established based on the Energy Business Division of Carrot Group in 2019. The company specializes in photovoltaic (PV), energy storage, and integrated PV-storage-charging solutions, with a strategic focus on the global new energy market.',
      introDesc2: 'With our headquarters in Chuzhou and a local subsidiary in Spain, we partner with top Chinese suppliers such as Gotion, CHINT, REPT, Sungrow, CATL, Tongwei, LONGi and Astronergy to deliver integrated system design, material supply, and construction services worldwide.',
      stats: {
        founded: { value: '2019', label: 'Company Founded' },
        employees: { value: '12,000+', label: 'Global Employees' },
        revenue: { value: '$4.14B+', label: '2023 Revenue' },
        shipments: { value: '130GW+', label: 'Cumulative Shipments' },
      },
      blue: {
        label: 'Business Segments',
        title: 'Diversified Energy Solutions for a Sustainable Future',
        desc: 'Our core operations span key sectors including the automotive industry chain, Internet of Vehicles (IoV), intelligent equipment, and energy technology — covering vehicle manufacturing, solar panels, energy storage systems, and portable power solutions.',
        stat: 'Countries Served Worldwide',
      },
      feature2: {
        label: 'Partnerships',
        title: 'Trusted by Leading Global Partners',
        desc: 'We collaborate with renowned international enterprises including Nissan, Karry, MG, and other major automotive brands. Our overseas business spans Europe, the Middle East, Southeast Asia, and Africa, with five KD (Knock-Down) factories already invested and constructed.',
        cta: 'Explore Products',
      },
      timeline: {
        title: 'Our Journey',
        items: [
          { date: 'May 2019', event: 'Coronet was Established' },
          { date: 'Sep 2022', event: 'Chuzhou Headquarters Founded' },
          { date: 'Jul 2024', event: 'WMI Code Obtained' },
          { date: 'Dec 2024', event: 'Spain Company Established' },
          { date: 'Aug 2025', event: 'Smart Equipment Division Founded' },
          { date: 'Jan 2026', event: 'WMI Dual Certification' },
        ],
      },
      partners: {
        title: 'Key Partners & Suppliers',
        subtitle: 'Working with industry leaders to deliver quality and innovation',
        companies: 'Gotion · CHINT · REPT · Sungrow · CATL · Tongwei · LONGi · Astronergy',
      },
      energy: {
        title: 'Energy Storage Solutions',
        subtitle: 'Comprehensive energy storage products for residential and commercial applications',
        residential: {
          title: 'Residential Systems',
          desc: 'Integrated PV-storage systems with high-efficiency inverters (3-6kW) and batteries (5-20kWh) for home energy self-sufficiency.',
        },
        commercial: {
          title: 'Commercial & Industrial',
          desc: 'Large-scale energy storage systems (50kW-1MW/2MWh) for commercial buildings, factories, and utility-scale applications.',
        },
        portable: {
          title: 'Portable Power',
          desc: 'Lightweight portable power stations (0.5-2kWh) with foldable solar panels (100-400W) for outdoor adventures and emergency use.',
        },
      },
      news: { title: 'Newest News', viewAll: 'View All' },
    },
    contact: {
      title: 'Get In Touch',
      subtitle: "Have questions about our products? Send us a message and we'll get back to you within 24 hours.",
      tabs: { visit: 'Visit', admin: 'Administration', cert: 'Certification', func: 'Functionality' },
      form: { title: 'Get In Touch', subtitle: 'Have questions about our products? Send us a message and we\'ll get back to you within 24 hours.', name: 'Your Name', email: 'Email Address', phone: 'Phone', subject: 'Subject', company: 'Company Name', message: 'Your Message', submit: 'Send Message', success: 'Message sent successfully! We will contact you soon.', privacy: 'I accept the privacy policy and consent to the processing of my data.' },
      info: { title: 'Contact Information', email: 'info@coronet-powerbank.com', offices: 'Germany / USA / China Office' },
      offices: { title: 'Find Us', viewMap: 'View on Map' },
    },
    footer: { rights: 'All rights reserved.', tagline: 'Your trusted portable energy partner.', copyright: 'Copyright © 2026 Coronet Energy Technology Co., Ltd.', icp: 'Anhui ICP Registration No. 2025103011-1', privacyPolicy: 'Privacy Policy', cookieNotice: 'Cookie Notice', cookieSettings: 'Cookie Settings' },
    privacy: {
    title: 'Privacy Policy',
    updated: 'Last Updated: August 10, 2026',
    intro: [
      'This Privacy Policy belongs to Coronet Energy Technology Co., Ltd ("we", "us", "our"). This policy explains how we collect, use, store, and protect personal data when you visit our website and submit inquiries through our contact form. This policy complies with the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other applicable global privacy laws.',
      'Our website does not require user registration, does not create user accounts, does not display advertisements, and does not collect geolocation data from visitors.',
      'By accessing and using our website, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use our website.',
      'For privacy-related inquiries, please contact us at any time.'
    ],
    company: {
      heading: 'Company Information',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Guomao Zhigu Building, Baohe District, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    },
    sections: [
      {
        id: 1,
        title: '1. Information We Collect',
        blocks: [
          { type: 'text', text: 'We only collect two types of information:' },
          { type: 'subsection', title: '1.1 Information you voluntarily submit', text: 'When you submit a contact or inquiry form on our website, we may collect your name and email address. This information is submitted actively by you for business consultation and communication purposes.' },
          { type: 'subsection', title: '1.2 Automatically collected technical data', text: 'We automatically collect basic anonymous technical data for website security, stability, and statistical analysis, including browser type, device type, website access time, visited pages, and anonymous IP information.' },
          { type: 'text', text: 'We do not collect precise geographic location data, device identifiers, or sensitive personal data.' }
        ]
      },
      {
        id: 2,
        title: '2. How We Use Your Information',
        blocks: [
          { type: 'text', text: 'We use collected data only for the following legitimate business purposes:' },
          { type: 'bullets', items: ['To reply to your product inquiries, technical support requests, and business consultations', 'To maintain normal website operation, troubleshoot errors, and ensure website security', 'To analyze anonymous website traffic data for website optimization', 'To comply with legal and regulatory obligations'] },
          { type: 'text', text: 'We do not use your data for targeted advertising, marketing push, or user profiling.' }
        ]
      },
      {
        id: 3,
        title: '3. Legal Basis for Data Processing (GDPR)',
        blocks: [
          { type: 'text', text: 'For visitors located in the European Economic Area (EEA), United Kingdom, and other GDPR-regulated regions, we process your personal data based on the following legal bases:' },
          { type: 'bullets', items: ['Consent: You voluntarily submit your contact information and agree to our data processing rules', 'Legitimate Interest: Normal website operation, business communication, and website optimization, which do not override your privacy rights'] }
        ]
      },
      {
        id: 4,
        title: '4. Cookies and Tracking Technologies',
        blocks: [
          { type: 'text', text: 'Our website uses standard website cookies and similar tracking technologies. We use strictly necessary cookies for basic website operation, and optional analytical cookies with user consent.' },
          { type: 'text', text: 'We do not use advertising cookies or third-party tracking pixels for commercial promotion.' },
          { type: 'text', text: 'You may manage, disable, or withdraw your cookie consent at any time through our website\'s Cookie Settings entry. Detailed cookie classification and usage rules are stated in our independent Cookie Notice.' }
        ]
      },
      {
        id: 5,
        title: '5. Data Retention Period',
        blocks: [
          { type: 'text', text: 'We retain your personal data only for the period necessary to fulfill the purposes for which it was collected:' },
          { type: 'bullets', items: ['Contact form submissions: We retain inquiry data for up to 24 months to facilitate follow-up communication and business relationship management', 'Technical log data: We retain anonymous technical data for up to 12 months for security analysis and website optimization', 'Cookie data: Necessary cookies are retained for the duration of your browsing session; analytics cookies are retained for up to 24 months as stated in our Cookie Notice'] },
          { type: 'text', text: 'After the retention period expires, your personal data will be permanently deleted or anonymized in accordance with applicable laws.' }
        ]
      },
      {
        id: 6,
        title: '6. Data Processors and Third-Party Services',
        blocks: [
          { type: 'text', text: 'To provide our services, we may share your data with the following third-party processors under strict contractual agreements:' },
          { type: 'bullets', items: ['Cloud hosting providers for server infrastructure and data storage', 'Email service providers for business communication delivery', 'Google Analytics (only with your explicit consent) for anonymous website traffic analysis'] },
          { type: 'text', text: 'We ensure all data processors meet GDPR compliance requirements and only process your data for the purposes we specify.' }
        ]
      },
      {
        id: 7,
        title: '7. Data Sharing and Disclosure',
        blocks: [
          { type: 'text', text: 'We do not sell, rent, or trade your personal data to any third parties for commercial purposes.' },
          { type: 'text', text: 'We may only disclose your data in the following situations:' },
          { type: 'bullets', items: ['Required by laws, regulations, or official legal procedures', 'Necessary to protect our legitimate rights, property, and website security'] }
        ]
      },
      {
        id: 8,
        title: '8. User Privacy Rights',
        blocks: [
          { type: 'text', text: 'If you are located in the EEA, UK, Switzerland, Canada, California, or other regions with privacy protection laws, you have the following rights:' },
          { type: 'bullets', items: ['Right to access your personal data', 'Right to correct inaccurate personal data', 'Right to delete your personal data (right to be forgotten)', 'Right to restrict data processing', 'Right to withdraw consent at any time', 'Right to data portability (receive your data in a structured format)'] },
          { type: 'text', text: 'You may submit a privacy request via our email: info@coronetenergy.com. We will respond to your valid request within the time required by applicable laws (typically within 30 days).' }
        ]
      },
      {
        id: 9,
        title: '9. Do Not Track (DNT)',
        blocks: [
          { type: 'text', text: 'We respect browser Do Not Track settings. Currently, there is no unified industry standard for DNT signal implementation, so we will continuously follow industry updates and adjust our privacy practices accordingly.' }
        ]
      },
      {
        id: 10,
        title: '10. Policy Updates',
        blocks: [
          { type: 'text', text: 'We may update this Privacy Policy irregularly to adapt to business adjustments and legal updates. The updated policy will be posted on this page with the latest revision date. Continued use of the website after updates constitutes acceptance of the new policy.' }
        ]
      },
      {
        id: 11,
        title: '11. Contact Us',
        blocks: [
          { type: 'text', text: 'If you have any questions, suggestions, or privacy rights requests regarding this Privacy Policy, please contact us:' }
        ]
      }
    ],
    contact: {
      title: 'Contact Us',
      desc: '',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Guomao Zhigu Building, Baohe District, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  },
  cookieNotice: {
    title: 'Cookie Notice',
    updated: 'Last Updated: August 10, 2026',
    intro: [
      'This Cookie Notice applies to the website of Coronet Energy Technology Co., Ltd (hereinafter referred to as "we", "us", or "our"). It explains what cookies are, which types of cookies we use on our website, how we use them, and how you can manage your cookie preferences. This notice complies with the EU ePrivacy Directive, GDPR, and international privacy standards.',
      'This Cookie Notice is a supplementary document to our Privacy Policy.'
    ],
    sections: [
      {
        id: 1,
        title: '1. What Are Cookies',
        blocks: [
          { type: 'text', text: 'Cookies are small text files stored on your device (computer, tablet, mobile phone) when you visit a website. They enable the website to recognize your device, remember basic browsing preferences, improve website functionality, and collect anonymous traffic data. Cookies do not access, read, or store your personal private files on your device.' }
        ]
      },
      {
        id: 2,
        title: '2. Cookies We Use on This Website',
        blocks: [
          { type: 'text', text: 'Our website does not use any advertising cookies, marketing cookies, or targeting tracking cookies. We only use two categories of cookies:' },
          { type: 'subsection', title: '2.1 Strictly Necessary Cookies', text: 'These cookies are essential for the basic operation of the website. They enable core functions such as page navigation, access control, and security verification. Strictly necessary cookies do not require user consent and cannot be disabled, as disabling them will cause the website to function abnormally.' },
          { type: 'text', text: 'Purpose: Maintain basic website stability and security.' },
          { type: 'text', text: 'Retention Period: Session-based or short-term storage.' },
          { type: 'subsection', title: '2.2 Analytical Cookies (Optional)', text: 'With your explicit consent, we use anonymous analytical cookies (Google Analytics) to collect anonymous traffic data, including page viewing volume, visitor behavior, and website operation status. All data collected is anonymized and cannot identify any individual user.' },
          { type: 'text', text: 'Purpose: Analyze website traffic, optimize website structure and user experience.' },
          { type: 'text', text: 'Retention Period: Up to 24 months.' },
          { type: 'text', text: 'No Marketing Cookies: We do not use any cookies for personalized advertising, user profiling, or commercial promotion.' }
        ]
      },
      {
        id: 3,
        title: '3. Third-Party Cookies',
        blocks: [
          { type: 'text', text: 'The only third-party service we use is Google Analytics, which uses analytical cookies for anonymous website statistics. All third-party data processing is subject to Google\'s Privacy Policy. We do not cooperate with any other advertising or tracking third-party platforms.' }
        ]
      },
      {
        id: 4,
        title: '4. How to Manage & Withdraw Cookie Consent',
        blocks: [
          { type: 'text', text: 'You have the full right to accept, refuse, or modify your cookie preferences at any time.' },
          { type: 'text', text: 'Modify Cookie Settings: Please click the Cookie Settings link in the website footer to reopen the consent panel and update your choices.' },
          { type: 'text', text: 'You may also disable cookies through your browser\'s built-in privacy settings. Please note that disabling analytical cookies will not affect your normal use of this website.' }
        ]
      },
      {
        id: 5,
        title: '5. Updates to This Notice',
        blocks: [
          { type: 'text', text: 'We may update this Cookie Notice occasionally to match website function adjustments and legal requirements. The latest version will be published on this page with an updated date.' }
        ]
      },
      {
        id: 6,
        title: '6. Contact Us',
        blocks: [
          { type: 'text', text: 'If you have any questions about our cookie usage rules, please contact us:' }
        ]
      }
    ],
    contact: {
      title: 'Contact Us',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Guomao Zhigu Building, Baohe District, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  }
},
  zh: {
    nav: { home: '首页', products: '产品中心', productList: '产品列表', cases: '应用场景', about: '关于我们', contact: '联系我们', news: '新闻动态' },
    news: {
      title: '新闻与洞察',
      subtitle: '了解便携式能源技术和行业趋势的最新发展。',
      readMore: '阅读更多',
      mostVisited: '最新新闻',
      noArticles: '该分类下暂无文章。',
      all: '全部',
    },
    newsDetail: {
      back: '返回新闻',
      previous: '上一篇',
      next: '下一篇',
    },
    lang: { switch: 'English', current: '中文' },
    home: {
      hero: { title: '为你的冒险赋能', subtitle: '面向户外、应急和商业用途的大容量便携式电源站', ctaQuote: '获取报价', ctaCatalog: '探索产品', ctaVideo: '观看视频' },
      trust: { label: '全球 500+ 企业信赖之选' },
      products: { title: '精选产品', subtitle: '面向企业客户的标准型号与定制解决方案', viewAll: '查看全部产品' },
      about: {
        label: '关于 Coronet',
        title: '用每一台电源站为世界赋能',
        desc: 'Coronet 是一家专业的户外便携式电源站和移动电源制造商。自 2020 年以来，我们已帮助全球数百家 B2B 合作伙伴利用便携式能源解决方案为其业务和客户赋能。',
        cta: '了解更多',
      },
      advantages: {
        title: '为什么选择我们',
        items: [
          { title: '高容量电池', desc: '最高 50,000mAh，满足长时间户外使用' },
          { title: '快速充电', desc: '最新快充技术，最高支持 65W' },
          { title: '全球配送', desc: '覆盖 50+ 国家的可靠物流配送' },
          { title: '定制 OEM', desc: '为 B2B 合作伙伴提供定制品牌与设计支持' },
        ],
      },
      scenarios: {
        title: '随时随地，电力随行',
        subtitle: '从露营旅行到应急备用，我们的产品在任何需要的地方提供可靠电力。',
      },
      testimonials: { title: '客户评价' },
      stats: { title: '用数据说话' },
      news: { title: '最新新闻', viewAll: '查看全部新闻' },
      cta: {
        title: '准备好为您的业务赋能了吗？',
        desc: '立即联系我们获取定制报价，或了解更多关于我们的 OEM/ODM 解决方案。',
        btnContact: '联系我们',
        btnQuote: '获取报价',
      },
    },
    products: {
      title: '移动电源产品目录',
      subtitle: '面向企业客户的标准型号与定制解决方案。',
      categories: { all: '全部产品', portableStorage: '便携式储能电源', residentialStorage: '户用储能系统', commercialStorage: '工商业储能系统', emergencyPower: '多功能应急电源' },
      viewCase: '查看详情',
      heroLabel: '户外电源站',
      heroTitle: '每一次冒险都有电力随行',
      heroSubtitle: '适用于户外、应急和商业用途的高容量便携式电源站。',
      heroCta: '获取报价',
      heroCta2: '探索产品',
      introLabel: '户外电源站',
      introTitle: '每个电源站都让您能够自主发电',
      introDesc: '我们的便携式电源站结合了高容量电池和多个输出端口，让您随时随地获得可靠电力。从露营旅行到应急备用，我们的产品始终提供稳定性能。',
      introStat: '拥有活跃分销合作伙伴的国家',
      introCta: '产品列表',
      featureLabel: '主要特点',
      featureTitle: '我们为您的家庭高效存储能源',
      featureCta: '联系我们',
      bannerTitle: '使用便携式电源站和智能能源，电费最高可节省60%',
      bannerDesc: '我们的解决方案帮助企业和消费者降低能源成本，同时保持可靠的电力供应。',
      chartTitle: '长期节能效果',
      chartSubtitle: '使用我们的电源站平均每月节省的百分比',
      cardsCta: '获取报价',
      splitTitle: '出售您不用的能源',
      splitDesc: '我们先进的便携式电源站允许企业存储多余能源并在需要时使用，创造额外收入来源并减少对电网的依赖。',
      splitCta: '了解更多',
      newsTitle: '新闻与出版物',
      newsViewAll: '查看全部',
      moreProducts: '更多产品',
    },
    productList: {
      heroTitle: '产品目录',
      heroSubtitle: '便携式移动电源与户外储能解决方案',
      heroCta: '获取定制报价',
      viewDetails: '查看详情',
      features: {
        highCapacity: '大容量',
        fastCharging: '快速充电',
        outdoorReady: '户外适用',
        customOem: '定制 OEM',
      },
    },
    productDetail: {
      subtitle: '5000mAh 大容量户外储能电源',
      output: '输出功率',
      compatible: '兼容容量',
      requestQuote: '获取报价',
      capacity: '容量',
      acOutput: '交流输出',
      dcOutput: '直流输出',
      chargingTime: '充电时间',
      productFeatures: '产品特性',
      techSpecs: '技术规格',
      parameter: '参数',
      specification: '规格',
      customize: '定制此产品',
      yourName: '您的姓名',
      companyName: '公司名称',
      techSpecLabels: {
        batteryType: '电池类型',
        outputPower: '输出功率',
        capacity: '容量',
        chargingMethod: '充电方式',
        operatingTemp: '工作温度',
        weight: '重量',
      },
      quantity: '数量',
      email: '邮箱',
      requirements: '产品需求',
      sendInquiry: '发送询价',
      inquirySent: '询价已发送！我们将尽快与您联系。',
      batteryType: '电池类型',
      outputPower: '输出功率',
      chargingMethod: '充电方式',
      operatingTemp: '工作温度',
      weight: '重量',
      callUs: '电话联系',
      emailUs: '邮件联系',
      relatedProducts: '相关产品',
      viewDetails: '查看详情',
    },
    cases: {
      title: '全球移动电源项目',
      subtitle: '为零售、工业、活动和分销提供可靠的便携式能源解决方案。',
      regions: { europe: '欧洲', northAmerica: '北美', asiaPacific: '亚太' },
      viewCase: '查看案例',
      trusted: '定制移动电源订单的可靠供应商。',
    },
    caseDetail: {
      back: '返回案例',
      related: '相关案例',
      viewDetails: '查看详情',
      previous: '上一个案例',
      next: '下一个案例',
      projectInfo: '项目信息',
      regions: '覆盖地区',
      solutions: '解决方案',
      outdoorStorage: '户外储能系统',
      application: '应用场景',
      scenarios: '露营、应急、工业',
      contactDesc: '了解更多关于此案例的详细信息，或获取定制化解决方案。',
    },
    about: {
      title: '关于我们的移动电源工厂',
      subtitle: '我们为全球的 B2B 合作伙伴设计和制造便携式移动电源、快速充电器和储能解决方案。',
      heroSubtitle: '面向全球 B2B 合作伙伴的户外电源解决方案',
      introLabel: '户外电源解决方案',
      introTitle: '我们是便携式电源与储能专家',
      introDesc: '合肥卡罗特能源有限公司成立于 2019 年，基于卡罗特集团能源事业部组建。公司专注于光伏、储能及光储充一体化解决方案，面向全球新能源市场。',
      introDesc2: '总部位于滁州，在西班牙设有本地子公司。我们与国轩高科、正泰、瑞浦兰钧、阳光电源、宁德时代、通威、隆基、正泰新能源等顶级供应商合作，为全球客户提供系统设计、物资供应和施工服务。',
      stats: {
        founded: { value: '2019年', label: '公司成立' },
        employees: { value: '12,000+', label: '全球员工' },
        revenue: { value: '$41.4亿+', label: '2023年营收' },
        shipments: { value: '130GW+', label: '累计出货' },
      },
      blue: {
        label: '业务板块',
        title: '多元化能源解决方案，助力可持续未来',
        desc: '核心业务涵盖汽车产业链、车联网、智能装备和能源科技四大领域，覆盖整车制造、太阳能板、储能系统和便携式电源等产品线。',
        stat: '全球服务国家',
      },
      feature2: {
        label: '合作伙伴',
        title: '与全球领先企业携手共赢',
        desc: '与日产、开瑞、名爵等知名国际企业合作。海外业务覆盖欧洲、中东、东南亚和非洲，已投资建设 5 个 KD（散件组装）工厂。',
        cta: '探索产品',
      },
      timeline: {
        title: '发展历程',
        items: [
          { date: '2019年5月', event: '卡罗特成立' },
          { date: '2022年9月', event: '滁州总部成立' },
          { date: '2024年7月', event: '获得WMI代号' },
          { date: '2024年12月', event: '西班牙公司成立' },
          { date: '2025年8月', event: '智能装备事业部成立' },
          { date: '2026年1月', event: 'WMI双认证' },
        ],
      },
      partners: {
        title: '核心合作伙伴',
        subtitle: '与行业领先企业合作，提供优质创新的产品与服务',
        companies: '国轩高科 · 正泰 · 瑞浦兰钧 · 阳光电源 · 宁德时代 · 通威 · 隆基 · 正泰新能源',
      },
      energy: {
        title: '储能解决方案',
        subtitle: '面向户用和工商业的全方位储能产品',
        residential: {
          title: '户用系统',
          desc: '集成光储系统，配备高效逆变器（3-6kW）和电池（5-20kWh），实现家庭能源自给自足。',
        },
        commercial: {
          title: '工商业储能',
          desc: '大型储能系统（50kW-1MW/2MWh），应用于商业建筑、工厂和公用事业场景。',
        },
        portable: {
          title: '便携式电源',
          desc: '轻量化便携电源站（0.5-2kWh），搭配折叠太阳能板（100-400W），适合户外探险和应急使用。',
        },
      },
      news: { title: '最新新闻', viewAll: '查看全部' },
    },
    contact: {
      title: '联系我们',
      subtitle: '对我们的产品有疑问？请发送消息，我们将在 24 小时内回复您。',
      tabs: { visit: '访问咨询', admin: '行政管理', cert: '认证与法规', func: '功能支持' },
      form: { title: '联系我们', subtitle: '对我们的产品有疑问？请发送消息，我们将在 24 小时内回复您。', name: '您的姓名', email: '电子邮箱', phone: '电话', subject: '主题', company: '公司名称', message: '留言内容', submit: '发送消息', success: '消息发送成功！我们将尽快与您联系。', privacy: '我同意隐私政策并同意处理我的数据。' },
      info: { title: '联系信息', email: 'info@coronet-powerbank.com', offices: '德国 / 美国 / 中国办公室' },
      offices: { title: '找到我们', viewMap: '在地图上查看' },
    },
    footer: { rights: '版权所有。', tagline: '您值得信赖的便携式能源合作伙伴。', copyright: 'Copyright © 2026 合肥卡罗特能源科技有限公司', icp: '皖ICP备2025103011号-1', privacyPolicy: '隐私政策', cookieNotice: 'Cookie声明', cookieSettings: 'Cookie设置' },
    privacy: {
    title: '隐私政策',
    updated: '最后更新：2026年8月10日',
    intro: [
      '本隐私政策属于合肥卡罗特能源科技有限公司（以下简称"我们"）。本政策说明了当您访问我们的网站并通过联系表单提交咨询时，我们如何收集、使用、存储和保护个人数据。本政策符合《通用数据保护条例》（GDPR）、《加州消费者隐私法案》（CCPA）及其他适用的全球隐私法律。',
      '我们的网站不需要用户注册，不创建用户账户，不展示广告，也不收集访客的地理位置数据。',
      '访问和使用我们的网站即表示您同意本隐私政策中描述的做法。如果您不同意，请不要使用我们的网站。',
      '如需隐私相关咨询，请随时通过以下方式与我们联系。'
    ],
    company: {
      heading: '公司信息',
      name: '合肥卡罗特能源科技有限公司',
      address: '中国安徽省合肥市包河区国贸智谷大厦',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    },
    sections: [
      {
        id: 1,
        title: '1. 我们收集的信息',
        blocks: [
          { type: 'text', text: '我们仅收集两类信息：' },
          { type: 'subsection', title: '1.1 您主动提交的信息', text: '当您在我们的网站上提交联系或咨询表单时，我们可能会收集您的姓名和电子邮箱地址。这些信息由您主动提交，用于商务咨询和沟通目的。' },
          { type: 'subsection', title: '1.2 自动收集的技术数据', text: '我们自动收集基本的匿名技术数据，用于网站安全、稳定性和统计分析，包括浏览器类型、设备类型、网站访问时间、访问页面和匿名IP信息。' },
          { type: 'text', text: '我们不收集精确的地理位置数据、设备标识符或敏感个人数据。' }
        ]
      },
      {
        id: 2,
        title: '2. 我们如何使用您的信息',
        blocks: [
          { type: 'text', text: '我们仅将收集的数据用于以下合法商业目的：' },
          { type: 'bullets', items: ['回复您的产品咨询、技术支持请求和商务咨询', '维护网站正常运行、排查错误并确保网站安全', '分析匿名网站流量数据以优化网站', '遵守法律法规义务'] },
          { type: 'text', text: '我们不会将您的数据用于定向广告、营销推送或用户画像。' }
        ]
      },
      {
        id: 3,
        title: '3. 数据处理的法律依据（GDPR）',
        blocks: [
          { type: 'text', text: '对于位于欧洲经济区（EEA）、英国和其他GDPR管辖地区的访客，我们依据以下法律依据处理您的个人数据：' },
          { type: 'bullets', items: ['同意：您主动提交联系信息并同意我们的数据处理规则', '合法利益：正常网站运营、商务沟通和网站优化，不会覆盖您的隐私权'] }
        ]
      },
      {
        id: 4,
        title: '4. Cookie和跟踪技术',
        blocks: [
          { type: 'text', text: '我们的网站使用标准网站Cookie和类似跟踪技术。我们使用必要Cookie以确保网站基本运行，并在用户同意的情况下使用可选分析Cookie。' },
          { type: 'text', text: '我们不使用广告Cookie或第三方跟踪像素进行商业推广。' },
          { type: 'text', text: '您可以随时通过网站的Cookie设置入口管理、禁用或撤回您的Cookie同意。详细的Cookie分类和使用规则请参见我们独立的Cookie声明。' }
        ]
      },
      {
        id: 5,
        title: '5. 数据保留期限',
        blocks: [
          { type: 'text', text: '我们仅在收集数据的目的所必需的期限内保留您的个人数据：' },
          { type: 'bullets', items: ['联系表单提交：我们保留询价数据最长24个月，以方便后续沟通和业务关系管理', '技术日志数据：我们保留匿名技术数据最长12个月，用于安全分析和网站优化', 'Cookie数据：必要Cookie在您浏览会话期间保留；分析Cookie的保留期限最长24个月，详见我们的Cookie声明'] },
          { type: 'text', text: '保留期限届满后，您的个人数据将根据相关法律永久删除或匿名化处理。' }
        ]
      },
      {
        id: 6,
        title: '6. 数据处理器与第三方服务',
        blocks: [
          { type: 'text', text: '为提供我们的服务，我们可能会在严格的合同约定下与以下第三方处理器共享您的数据：' },
          { type: 'bullets', items: ['云托管服务商，用于服务器基础设施和数据存储', '邮件服务提供商，用于业务通信投递', 'Google Analytics（仅在您明确同意的情况下），用于匿名网站流量分析'] },
          { type: 'text', text: '我们确保所有数据处理器符合GDPR合规要求，且仅为我们指定的目的处理您的数据。' }
        ]
      },
      {
        id: 7,
        title: '7. 数据共享与披露',
        blocks: [
          { type: 'text', text: '我们不会将您的个人数据出售、出租或交易给任何第三方用于商业目的。' },
          { type: 'text', text: '我们仅在以下情况下可能披露您的数据：' },
          { type: 'bullets', items: ['法律法规或官方法律程序要求', '保护我们合法权益、财产和网站安全所必需'] }
        ]
      },
      {
        id: 8,
        title: '8. 用户隐私权利',
        blocks: [
          { type: 'text', text: '如果您位于欧洲经济区、英国、瑞士、加拿大、加州或其他有隐私保护法律的地区，您享有以下权利：' },
          { type: 'bullets', items: ['访问您个人数据的权利', '更正不准确个人数据的权利', '删除您个人数据的权利（被遗忘权）', '限制数据处理的权利', '随时撤回同意的权利', '数据可携带权（以结构化格式接收您的数据）'] },
          { type: 'text', text: '您可以通过我们的邮箱提交隐私请求：info@coronetenergy.com。我们将在相关法律要求的时间内响应您的有效请求（通常在30天内）。' }
        ]
      },
      {
        id: 9,
        title: '9. 请勿跟踪（DNT）',
        blocks: [
          { type: 'text', text: '我们尊重浏览器的"请勿跟踪"设置。目前，关于DNT信号实施尚无统一的行业标准，因此我们将持续关注行业动态并相应调整隐私做法。' }
        ]
      },
      {
        id: 10,
        title: '10. 政策更新',
        blocks: [
          { type: 'text', text: '我们可能会不定期更新本隐私政策，以适应业务调整和法律更新。更新后的政策将在本页面发布，并标注最新修订日期。继续使用网站即表示您接受新的政策。' }
        ]
      },
      {
        id: 11,
        title: '11. 联系我们',
        blocks: [
          { type: 'text', text: '如果您对本隐私政策有任何疑问、建议或隐私权利请求，请通过以下方式联系我们：' }
        ]
      }
    ],
    contact: {
      title: '联系我们',
      desc: '',
      name: '合肥卡罗特能源科技有限公司',
      address: '中国安徽省合肥市包河区国贸智谷大厦',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  },
  cookieNotice: {
    title: 'Cookie 声明',
    updated: '最后更新：2026年8月10日',
    intro: [
      '本 Cookie 声明适用于合肥卡罗特能源科技有限公司（以下简称"我们"）的网站。它说明了什么是 Cookie、我们在网站上使用哪些类型的 Cookie、如何使用以及您如何管理 Cookie 偏好。本声明符合欧盟《隐私和电子通信指令》、GDPR 及国际隐私标准。',
      '本 Cookie 声明是我们隐私政策的补充文件。'
    ],
    sections: [
      {
        id: 1,
        title: '1. 什么是 Cookie',
        blocks: [
          { type: 'text', text: 'Cookie 是存储在您的设备（计算机、平板电脑、手机）上的小型文本文件，当您访问网站时创建。它们使网站能够识别您的设备、记住基本浏览偏好、改善网站功能并收集匿名流量数据。Cookie 不会访问、读取或存储您设备上的私人文件。' }
        ]
      },
      {
        id: 2,
        title: '2. 本网站使用的 Cookie',
        blocks: [
          { type: 'text', text: '我们的网站不使用任何广告 Cookie、营销 Cookie 或定向跟踪 Cookie。我们仅使用两类 Cookie：' },
          { type: 'subsection', title: '2.1 严格必要的 Cookie', text: '这些 Cookie 对网站的基本运行至关重要。它们实现页面导航、访问控制和安全验证等核心功能。严格必要的 Cookie 无需用户同意且无法禁用，因为禁用会导致网站功能异常。' },
          { type: 'text', text: '目的：维持网站基本稳定性和安全性。' },
          { type: 'text', text: '保留期限：会话级别或短期存储。' },
          { type: 'subsection', title: '2.2 分析 Cookie（可选）', text: '在您明确同意的情况下，我们使用匿名分析 Cookie（Google Analytics）收集匿名流量数据，包括页面浏览量、访客行为和网站运行状态。所有收集的数据都是匿名的，无法识别任何个人用户。' },
          { type: 'text', text: '目的：分析网站流量、优化网站结构和用户体验。' },
          { type: 'text', text: '保留期限：最长 24 个月。' },
          { type: 'text', text: '无营销 Cookie：我们不使用任何 Cookie 进行个性化广告、用户画像或商业推广。' }
        ]
      },
      {
        id: 3,
        title: '3. 第三方 Cookie',
        blocks: [
          { type: 'text', text: '我们使用的唯一第三方服务是 Google Analytics，它使用分析 Cookie 进行匿名网站统计。所有第三方数据处理均受 Google 隐私政策约束。我们不与任何其他广告或跟踪第三方平台合作。' }
        ]
      },
      {
        id: 4,
        title: '4. 如何管理和撤回 Cookie 同意',
        blocks: [
          { type: 'text', text: '您拥有随时接受、拒绝或修改 Cookie 偏好的完整权利。' },
          { type: 'text', text: '修改 Cookie 设置：请点击网站底部的 Cookie 设置链接重新打开同意面板并更新您的选择。' },
          { type: 'text', text: '您也可以通过浏览器内置的隐私设置禁用 Cookie。请注意，禁用分析 Cookie 不会影响您对本网站的正常使用。' }
        ]
      },
      {
        id: 5,
        title: '5. 本声明的更新',
        blocks: [
          { type: 'text', text: '我们可能会不定期更新本 Cookie 声明，以匹配网站功能调整和法律要求。最新版本将在本页面发布，并标注更新日期。' }
        ]
      },
      {
        id: 6,
        title: '6. 联系我们',
        blocks: [
          { type: 'text', text: '如果您对我们的 Cookie 使用规则有任何疑问，请通过以下方式联系我们：' }
        ]
      }
    ],
    contact: {
      title: '联系我们',
      name: '合肥卡罗特能源科技有限公司',
      address: '中国安徽省合肥市包河区国贸智谷大厦',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  }
},
  es: {
    nav: { home: 'Inicio', products: 'Productos', productList: 'Lista de Productos', cases: 'Casos', about: 'Quiénes somos', contact: 'Contacto', news: 'Noticias' },
    news: {
      title: 'Noticias y Perspectivas',
      subtitle: 'Manténgase actualizado con los últimos desarrollos en tecnología de energía portátil y tendencias de la industria.',
      readMore: 'Leer Más',
      mostVisited: 'Noticias Recientes',
      noArticles: 'No hay artículos en esta categoría.',
      all: 'Todos',
    },
    newsDetail: {
      back: 'Volver a Noticias',
      previous: 'Artículo Anterior',
      next: 'Artículo Siguiente',
    },
    lang: { switch: 'English', current: 'Español' },
    home: {
      hero: {
        title: 'Energía para tu Aventura',
        subtitle: 'Estaciones de energía portátil de alta capacidad para uso exterior, emergencia y comercial.',
        ctaQuote: 'Solicitar Presupuesto',
        ctaCatalog: 'Explorar Productos',
        ctaVideo: 'Ver Video',
      },
      trust: {
        label: 'Confiado por más de 500 empresas en todo el mundo',
      },
      products: { title: 'Productos Destacados', subtitle: 'Modelos estándar y soluciones personalizadas para compradores empresariales', viewAll: 'Ver Todos los Productos' },
      about: {
        label: 'Sobre Coronet',
        title: 'Energizando el Mundo, Una Estación a la Vez',
        desc: 'Coronet es un fabricante profesional de estaciones de energía portátil exterior y baterías portátiles. Desde 2020, hemos ayudado a cientos de socios B2B en todo el mundo a aprovechar soluciones de energía portátil para potenciar sus negocios y clientes.',
        cta: 'Conoce Más Sobre Nosotros',
      },
      advantages: {
        title: 'Por Qué Elegirnos',
        items: [
          { title: 'Alta Capacidad', desc: 'Hasta 50,000mAh para uso prolongado al aire libre' },
          { title: 'Carga Rápida', desc: 'Última tecnología de carga rápida hasta 65W' },
          { title: 'Entrega Global', desc: 'Envíos a más de 50 países con logística confiable' },
          { title: 'OEM Personalizado', desc: 'Soporte de marca y diseño personalizado para socios B2B' },
        ],
      },
      scenarios: {
        title: 'Energía en Cualquier Lugar, en Cualquier Momento',
        subtitle: 'Desde viajes de camping hasta respaldo de emergencia, nuestros productos ofrecen energía confiable donde la necesite.',
      },
      testimonials: {
        title: 'Lo Que Dicen Nuestros Clientes',
      },
      stats: {
        title: 'Nuestro Impacto en Números',
      },
      news: { title: 'Últimas Noticias', viewAll: 'Ver Todas las Noticias' },
      cta: {
        title: '¿Listo para Energizar su Negocio?',
        desc: 'Contáctenos hoy para un presupuesto personalizado o para conocer más sobre nuestras soluciones OEM/ODM.',
        btnContact: 'Contáctenos',
        btnQuote: 'Solicitar Presupuesto',
      },
    },
    products: {
      title: 'Catálogo de Productos de Energía Portátil',
      subtitle: 'Modelos estándar y soluciones personalizadas para compradores empresariales.',
      categories: { all: 'Todos los Productos', portableStorage: 'Almacenamiento Portátil', residentialStorage: 'Sistema de Almacenamiento Residencial', commercialStorage: 'Sistema de Almacenamiento Comercial', emergencyPower: 'Energía de Emergencia Multifuncional' },
      viewCase: 'Ver Detalles',
      heroLabel: 'Estaciones de Energía Exterior',
      heroTitle: 'Energía Portátil para Cada Aventura',
      heroSubtitle: 'Estaciones de energía portátiles de alta capacidad para uso exterior, emergencia y comercial.',
      heroCta: 'Solicitar Presupuesto',
      heroCta2: 'Explorar Productos',
      introLabel: 'Estaciones de Energía Exterior',
      introTitle: 'Cada Estación de Energía le Permite Generar su Propia Electricidad',
      introDesc: 'Nuestras estaciones de energía portátil combinan baterías de alta capacidad con múltiples puertos de salida, permitiéndole acceder a energía confiable en cualquier lugar. Desde viajes de camping hasta respaldo de emergencia, nuestros productos ofrecen un rendimiento constante.',
      introStat: 'Países con socios de distribución activos',
      introCta: 'Catálogo de Productos',
      featureLabel: 'Características Clave',
      featureTitle: 'Almacenamos Energía de Forma Eficiente para su Hogar',
      featureCta: 'Contáctenos',
      bannerTitle: 'Con una Estación de Energía Portátil y Energía Inteligente, Ahorre Hasta un 60% en su Factura de Electricidad',
      bannerDesc: 'Nuestras soluciones ayudan a empresas y consumidores a reducir costos de energía mientras mantienen un suministro confiable.',
      chartTitle: 'Ahorro de Energía con el Tiempo',
      chartSubtitle: 'Porcentaje promedio de ahorro mensual con nuestras estaciones de energía',
      cardsCta: 'Obtener Presupuesto',
      splitTitle: 'Venda la Energía que No Usa',
      splitDesc: 'Nuestras avanzadas estaciones de energía portátil permiten a las empresas almacenar el exceso de energía y usarlo cuando sea necesario, creando fuentes de ingresos adicionales y reduciendo la dependencia de la red.',
      splitCta: 'Más Información',
      newsTitle: 'Noticias y Publicaciones',
      newsViewAll: 'Ver Todo',
      moreProducts: 'Más Productos',
    },
    productList: {
      heroTitle: 'Catálogo de Productos',
      heroSubtitle: 'Baterías Portátiles y Soluciones de Almacenamiento de Energía Exterior',
      heroCta: 'Solicitar un Presupuesto Personalizado',
      viewDetails: 'Ver Detalles',
      features: {
        highCapacity: 'Alta Capacidad',
        fastCharging: 'Carga Rápida',
        outdoorReady: 'Listo para Exterior',
        customOem: 'OEM Personalizado',
      },
    },
    productDetail: {
      subtitle: 'Almacenamiento de Energía Exterior de Alta Capacidad 5000mAh',
      output: 'Salida',
      compatible: 'Capacidad Compatible',
      requestQuote: 'Solicitar Presupuesto',
      capacity: 'Capacidad',
      acOutput: 'Salida AC',
      dcOutput: 'Salida DC',
      chargingTime: 'Tiempo de Carga',
      productFeatures: 'Características del Producto',
      techSpecs: 'Especificaciones Técnicas',
      parameter: 'Parámetro',
      specification: 'Especificación',
      customize: 'Personalizar Este Producto',
      yourName: 'Su Nombre',
      companyName: 'Nombre de la Empresa',
      techSpecLabels: {
        batteryType: 'Tipo de Batería',
        outputPower: 'Potencia de Salida',
        capacity: 'Capacidad',
        chargingMethod: 'Método de Carga',
        operatingTemp: 'Temperatura de Operación',
        weight: 'Peso',
      },
      quantity: 'Cantidad',
      email: 'Correo Electrónico',
      requirements: 'Requisitos del Producto',
      sendInquiry: 'Enviar Consulta',
      inquirySent: '¡Consulta enviada! Nos pondremos en contacto pronto.',
      batteryType: 'Tipo de Batería',
      outputPower: 'Potencia de Salida',
      chargingMethod: 'Método de Carga',
      operatingTemp: 'Temperatura de Funcionamiento',
      weight: 'Peso',
      callUs: 'Llámenos',
      emailUs: 'Envíenos un Correo',
      relatedProducts: 'Productos Relacionados',
      viewDetails: 'Ver Detalles',
    },
    cases: {
      title: 'Proyectos Globales de Energía Portátil',
      subtitle: 'Soluciones de energía portátil confiables para retail, industria, eventos y distribución.',
      regions: { europe: 'Europa', northAmerica: 'Norteamérica', asiaPacific: 'Asia Pacífico' },
      viewCase: 'Ver Caso',
      trusted: 'Proveedor confiable para pedidos personalizados de baterías portátiles.',
    },
    caseDetail: {
      back: 'Volver a Casos',
      related: 'Casos Relacionados',
      viewDetails: 'Ver Detalles',
      previous: 'Caso Anterior',
      next: 'Caso Siguiente',
      projectInfo: 'Información del Proyecto',
      regions: 'Regiones',
      solutions: 'Soluciones',
      outdoorStorage: 'Sistema de Almacenamiento de Energía Exterior',
      application: 'Aplicación',
      scenarios: 'Camping, Emergencia, Industrial',
      contactDesc: 'Obtenga más información sobre este caso de estudio o consiga una solución personalizada.',
    },
    about: {
      title: 'Sobre Nuestra Fábrica de Energía Portátil',
      subtitle: 'Diseñamos y fabricamos baterías portátiles, cargadores rápidos y soluciones de almacenamiento de energía para socios B2B globales.',
      heroSubtitle: 'Soluciones de Energía Exterior para Socios B2B Globales',
      introLabel: 'Soluciones de Energía Exterior',
      introTitle: 'Somos Expertos en Energía Portátil y Almacenamiento',
      introDesc: 'Hefei Coronet Energy Co., Ltd. se estableció en 2019 basándose en la División de Negocios de Energía del Grupo Carrot. La empresa se especializa en energía fotovoltaica (PV), almacenamiento de energía y soluciones integradas de PV-almacenamiento-carga, con un enfoque estratégico en el mercado global de nuevas energías.',
      introDesc2: 'Con sede en Chuzhou y una subsidiaria local en España, nos asociamos con los principales proveedores chinos como Gotion, CHINT, REPT, Sungrow, CATL, Tongwei, LONGi y Astronergy para ofrecer diseño de sistemas integrados, suministro de materiales y servicios de construcción en todo el mundo.',
      stats: {
        founded: { value: '2019', label: 'Fundación de la Empresa' },
        employees: { value: '12.000+', label: 'Empleados Globales' },
        revenue: { value: '$4.14B+', label: 'Ingresos 2023' },
        shipments: { value: '130GW+', label: 'Envíos Acumulados' },
      },
      blue: {
        label: 'Segmentos de Negocio',
        title: 'Soluciones de Energía Diversificadas para un Futuro Sostenible',
        desc: 'Nuestras operaciones principales absectores clave, incluidas la cadena de la industria automotriz, Internet de los Vehículos (IoV), equipos inteligentes y tecnología energética — cubriendo fabricación de vehículos, paneles solares, sistemas de almacenamiento de energía y soluciones de energía portátil.',
        stat: 'Países Atendidos en Todo el Mundo',
      },
      feature2: {
        label: 'Asociaciones',
        title: 'Con la Confianza de Socios Globales Líderes',
        desc: 'Colaboramos con empresas internacionales reconocidas, incluidas Nissan, Karry, MG y otras marcas automotrices importantes. Nuestros negocios en el extranjero se extienden por Europa, Oriente Medio, Sudeste Asiático y África, con cinco fábricas KD (Knock-Down) ya invertidas y construidas.',
        cta: 'Explorar Productos',
      },
      timeline: {
        title: 'Nuestro Camino',
        items: [
          { date: 'Mayo 2019', event: 'Fundación de Coronet' },
          { date: 'Sep 2022', event: 'Sede de Chuzhou Establecida' },
          { date: 'Jul 2024', event: 'Código WMI Obtenido' },
          { date: 'Dic 2024', event: 'Empresa en España Establecida' },
          { date: 'Ago 2025', event: 'División de Equipos Inteligentes' },
          { date: 'Ene 2026', event: 'Doble Certificación WMI' },
        ],
      },
      partners: {
        title: 'Socios y Proveedores Clave',
        subtitle: 'Trabajando con líderes de la industria para ofrecer calidad e innovación',
        companies: 'Gotion · CHINT · REPT · Sungrow · CATL · Tongwei · LONGi · Astronergy',
      },
      energy: {
        title: 'Soluciones de Almacenamiento de Energía',
        subtitle: 'Productos integrales de almacenamiento de energía para aplicaciones residenciales y comerciales',
        residential: {
          title: 'Sistemas Residenciales',
          desc: 'Sistemas integrados de PV-almacenamiento con inversores de alta eficiencia (3-6kW) y baterías (5-20kWh) para la autosuficiencia energética del hogar.',
        },
        commercial: {
          title: 'Comercial e Industrial',
          desc: 'Sistemas de almacenamiento de energía a gran escala (50kW-1MW/2MWh) para edificios comerciales, fábricas y aplicaciones a nivel de servicios públicos.',
        },
        portable: {
          title: 'Energía Portátil',
          desc: 'Estaciones de energía portátiles ligeras (0.5-2kWh) con paneles solares plegables (100-400W) para aventuras al aire libre y uso de emergencia.',
        },
      },
      news: { title: 'Últimas Noticias', viewAll: 'Ver Todo' },
    },
    contact: {
      title: 'Contáctenos',
      subtitle: '¿Tiene preguntas sobre nuestros productos? Envíenos un mensaje y le responderemos en 24 horas.',
      tabs: { visit: 'Visita', admin: 'Administración', cert: 'Certificación', func: 'Funcionalidad' },
      form: { title: 'Contáctenos', subtitle: '¿Tiene preguntas sobre nuestros productos? Envíenos un mensaje y le responderemos en 24 horas.', name: 'Su Nombre', email: 'Correo Electrónico', phone: 'Teléfono', subject: 'Asunto', company: 'Nombre de la Empresa', message: 'Su Mensaje', submit: 'Enviar Mensaje', success: '¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.', privacy: 'Acepto la política de privacidad y consiento el procesamiento de mis datos.' },
      info: { title: 'Información de Contacto', email: 'info@coronet-powerbank.com', offices: 'Oficina Alemania / EE.UU. / China' },
      offices: { title: 'Encuéntrenos', viewMap: 'Ver en Mapa' },
    },
    footer: { rights: 'Todos los derechos reservados.', tagline: 'Su socio confiable de energía portátil.', copyright: 'Copyright © 2026 Coronet Energy Technology Co., Ltd.', icp: 'Registro ICP de Anhui No. 2025103011-1', privacyPolicy: 'Política de Privacidad', cookieNotice: 'Aviso de Cookies', cookieSettings: 'Configuración de Cookies' },
    privacy: {
    title: 'Política de Privacidad',
    updated: 'Última Actualización: 10 de Agosto de 2026',
    intro: [
      'Esta Política de Privacidad pertenece a Coronet Energy Technology Co., Ltd ("nosotros", "nuestro"). Esta política explica cómo recopilamos, usamos, almacenamos y protegemos los datos personales cuando visita nuestro sitio web y envía consultas a través de nuestro formulario de contacto. Esta política cumple con el Reglamento General de Protección de Datos (GDPR), la Ley de Privacidad del Consumidor de California (CCPA) y otras leyes de privacidad globales aplicables.',
      'Nuestro sitio web no requiere registro de usuario, no crea cuentas de usuario, no muestra publicidad y no recopila datos de geolocalización de los visitantes.',
      'Al acceder y utilizar nuestro sitio web, usted acepta las prácticas descritas en esta Política de Privacidad. Si no está de acuerdo, no utilice nuestro sitio web.',
      'Para consultas relacionadas con la privacidad, contáctenos en cualquier momento.'
    ],
    company: {
      heading: 'Información de la Empresa',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Edificio Guomao Zhigu, Distrito Baohe, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    },
    sections: [
      {
        id: 1,
        title: '1. Información que Recopilamos',
        blocks: [
          { type: 'text', text: 'Solo recopilamos dos tipos de información:' },
          { type: 'subsection', title: '1.1 Información que usted envía voluntariamente', text: 'Cuando envía un formulario de contacto o consulta en nuestro sitio web, podemos recopilar su nombre y dirección de correo electrónico. Esta información es enviada activamente por usted con fines de consulta y comunicación comercial.' },
          { type: 'subsection', title: '1.2 Datos técnicos recopilados automáticamente', text: 'Recopilamos automáticamente datos técnicos anónimos básicos para la seguridad, estabilidad y análisis estadístico del sitio web, incluidos el tipo de navegador, tipo de dispositivo, tiempo de acceso al sitio web, páginas visitadas e información de IP anónima.' },
          { type: 'text', text: 'No recopilamos datos de ubicación geográfica precisa, identificadores de dispositivo ni datos personales sensibles.' }
        ]
      },
      {
        id: 2,
        title: '2. Cómo Usamos su Información',
        blocks: [
          { type: 'text', text: 'Usamos los datos recopilados solo para los siguientes fines comerciales legítimos:' },
          { type: 'bullets', items: ['Responder a sus consultas de productos, solicitudes de soporte técnico y consultas comerciales', 'Mantener el funcionamiento normal del sitio web, solucionar errores y garantizar la seguridad del sitio web', 'Analizar datos de tráfico del sitio web anónimo para la optimización del sitio web', 'Cumplir con las obligaciones legales y regulatorias'] },
          { type: 'text', text: 'No usamos sus datos para publicidad dirigida, envío de marketing ni perfiles de usuario.' }
        ]
      },
      {
        id: 3,
        title: '3. Base Legal para el Procesamiento de Datos (GDPR)',
        blocks: [
          { type: 'text', text: 'Para los visitantes ubicados en el Área Económica Europea (EEA), Reino Unido y otras regiones reguladas por el GDPR, procesamos sus datos personales según las siguientes bases legales:' },
          { type: 'bullets', items: ['Consentimiento: Usted envía voluntariamente su información de contacto y acepta nuestras reglas de procesamiento de datos', 'Interés Legítimo: Operación normal del sitio web, comunicación comercial y optimización del sitio web, que no prevalecen sobre sus derechos de privacidad'] }
        ]
      },
      {
        id: 4,
        title: '4. Cookies y Tecnologías de Seguimiento',
        blocks: [
          { type: 'text', text: 'Nuestro sitio web utiliza cookies estándar y tecnologías de seguimiento similares. Utilizamos cookies estrictamente necesarias para el funcionamiento básico del sitio web y cookies analíticas opcionales con el consentimiento del usuario.' },
          { type: 'text', text: 'No utilizamos cookies publicitarias ni píxeles de seguimiento de terceros para promoción comercial.' },
          { type: 'text', text: 'Puede gestionar, deshabilitar o retirar su consentimiento de cookies en cualquier momento a través de la entrada de Configuración de Cookies de nuestro sitio web. La clasificación y las reglas de uso de cookies detalladas se indican en nuestro Aviso de Cookies independiente.' }
        ]
      },
      {
        id: 5,
        title: '5. Período de Retención de Datos',
        blocks: [
          { type: 'text', text: 'Retenemos sus datos personales solo durante el período necesario para cumplir con los fines para los cuales se recopilaron:' },
          { type: 'bullets', items: ['Envíos de formularios de contacto: Retenemos los datos de consulta durante un máximo de 24 meses para facilitar la comunicación de seguimiento y la gestión de relaciones comerciales', 'Datos de registro técnico: Retenemos los datos técnicos anónimos durante un máximo de 12 meses para análisis de seguridad y optimización del sitio web', 'Datos de cookies: Las cookies necesarias se retienen durante su sesión de navegación; las cookies analíticas se retienen durante un máximo de 24 meses como se indica en nuestro Aviso de Cookies'] },
          { type: 'text', text: 'Después de que expire el período de retención, sus datos personales se eliminarán permanentemente o se anonimizarán de acuerdo con las leyes aplicables.' }
        ]
      },
      {
        id: 6,
        title: '6. Procesadores de Datos y Servicios de Terceros',
        blocks: [
          { type: 'text', text: 'Para proporcionar nuestros servicios, podemos compartir sus datos con los siguientes procesadores de terceros bajo estrictos acuerdos contractuales:' },
          { type: 'bullets', items: ['Proveedores de alojamiento en la nube para infraestructura de servidores y almacenamiento de datos', 'Proveedores de servicios de correo electrónico para la entrega de comunicaciones comerciales', 'Google Analytics (solo con su consentimiento explícito) para el análisis de tráfico anónimo del sitio web'] },
          { type: 'text', text: 'Nos aseguramos de que todos los procesadores de datos cumplan con los requisitos de cumplimiento del GDPR y procesen sus datos solo para los fines que especificamos.' }
        ]
      },
      {
        id: 7,
        title: '7. Compartir y Divulgación de Datos',
        blocks: [
          { type: 'text', text: 'No vendemos, alquilamos ni intercambiamos sus datos personales a terceros con fines comerciales.' },
          { type: 'text', text: 'Solo podemos divulgar sus datos en las siguientes situaciones:' },
          { type: 'bullets', items: ['Requerido por leyes, regulaciones o procedimientos legales oficiales', 'Necesario para proteger nuestros derechos legítimos, propiedad y seguridad del sitio web'] }
        ]
      },
      {
        id: 8,
        title: '8. Derechos de Privacidad del Usuario',
        blocks: [
          { type: 'text', text: 'Si se encuentra en el EEA, Reino Unido, Suiza, Canadá, California u otras regiones con leyes de protección de la privacidad, tiene los siguientes derechos:' },
          { type: 'bullets', items: ['Derecho a acceder a sus datos personales', 'Derecho a corregir datos personales inexactos', 'Derecho a eliminar sus datos personales (derecho al olvido)', 'Derecho a restringir el procesamiento de datos', 'Derecho a retirar el consentimiento en cualquier momento', 'Derecho a la portabilidad de datos (recibir sus datos en un formato estructurado)'] },
          { type: 'text', text: 'Puede enviar una solicitud de privacidad a través de nuestro correo electrónico: info@coronetenergy.com. Responderemos a su solicitud válida dentro del tiempo requerido por las leyes aplicables (normalmente dentro de los 30 días).' }
        ]
      },
      {
        id: 9,
        title: '9. No Rastrear (DNT)',
        blocks: [
          { type: 'text', text: 'Respetamos la configuración de No Rastrear del navegador. Actualmente, no existe un estándar industrial unificado para la implementación de señales DNT, por lo que seguiremos continuamente las actualizaciones de la industria y ajustaremos nuestras prácticas de privacidad en consecuencia.' }
        ]
      },
      {
        id: 10,
        title: '10. Actualizaciones de Política',
        blocks: [
          { type: 'text', text: 'Podemos actualizar esta Política de Privacidad ocasionalmente para adaptarnos a ajustes comerciales y actualizaciones legales. La política actualizada se publicará en esta página con la fecha de revisión más reciente. El uso continuo del sitio web después de las actualizaciones constituye la aceptación de la nueva política.' }
        ]
      },
      {
        id: 11,
        title: '11. Contáctenos',
        blocks: [
          { type: 'text', text: 'Si tiene alguna pregunta, sugerencia o solicitud de derechos de privacidad con respecto a esta Política de Privacidad, contáctenos:' }
        ]
      }
    ],
    contact: {
      title: 'Contáctenos',
      desc: '',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Edificio Guomao Zhigu, Distrito Baohe, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  },
  cookieNotice: {
    title: 'Aviso de Cookies',
    updated: 'Última Actualización: 10 de Agosto de 2026',
    intro: [
      'Este Aviso de Cookies se aplica al sitio web de Coronet Energy Technology Co., Ltd (en adelante, "nosotros", "nuestro"). Explica qué son las cookies, qué tipos de cookies utilizamos en nuestro sitio web, cómo las usamos y cómo puede gestionar sus preferencias de cookies. Este aviso cumple con la Directiva de Privacidad y Comunicaciones Electrónicas de la UE, el GDPR y los estándares internacionales de privacidad.',
      'Este Aviso de Cookies es un documento complementario a nuestra Política de Privacidad.'
    ],
    sections: [
      {
        id: 1,
        title: '1. Qué Son las Cookies',
        blocks: [
          { type: 'text', text: 'Las cookies son pequeños archivos de texto almacenados en su dispositivo (computadora, tableta, teléfono móvil) cuando visita un sitio web. Permiten que el sitio web reconozca su dispositivo, recuerde las preferencias básicas de navegación, mejore la funcionalidad del sitio web y recopile datos de tráfico anónimo. Las cookies no acceden, leen ni almacenan sus archivos privados personales en su dispositivo.' }
        ]
      },
      {
        id: 2,
        title: '2. Cookies Que Usamos en Este Sitio Web',
        blocks: [
          { type: 'text', text: 'Nuestro sitio web no utiliza ninguna cookie de publicidad, cookie de marketing ni cookie de seguimiento dirigido. Solo utilizamos dos categorías de cookies:' },
          { type: 'subsection', title: '2.1 Cookies Estrictamente Necesarias', text: 'Estas cookies son esenciales para el funcionamiento básico del sitio web. Permiten funciones principales como la navegación de páginas, el control de acceso y la verificación de seguridad. Las cookies estrictamente necesarias no requieren consentimiento del usuario y no se pueden desactivar, ya que desactivarlas provocaría un funcionamiento anormal del sitio web.' },
          { type: 'text', text: 'Propósito: Mantener la estabilidad y seguridad básicas del sitio web.' },
          { type: 'text', text: 'Período de Conservación: Basado en sesión o almacenamiento a corto plazo.' },
          { type: 'subsection', title: '2.2 Cookies de Análisis (Opcionales)', text: 'Con su consentimiento explícito, utilizamos cookies de análisis anónimo (Google Analytics) para recopilar datos de tráfico anónimo, incluidos el volumen de visualización de páginas, el comportamiento de los visitantes y el estado operativo del sitio web. Todos los datos recopilados son anónimos y no pueden identificar a ningún usuario individual.' },
          { type: 'text', text: 'Propósito: Analizar el tráfico del sitio web, optimizar la estructura del sitio web y la experiencia del usuario.' },
          { type: 'text', text: 'Período de Conservación: Hasta 24 meses.' },
          { type: 'text', text: 'Sin Cookies de Marketing: No utilizamos ninguna cookie para publicidad personalizada, perfiles de usuario o promoción comercial.' }
        ]
      },
      {
        id: 3,
        title: '3. Cookies de Terceros',
        blocks: [
          { type: 'text', text: 'El único servicio de terceros que utilizamos es Google Analytics, que utiliza cookies de análisis para estadísticas de sitios web anónimas. Todo el procesamiento de datos de terceros está sujeto a la Política de Privacidad de Google. No cooperamos con ninguna otra plataforma de publicidad o seguimiento de terceros.' }
        ]
      },
      {
        id: 4,
        title: '4. Cómo Gestionar y Retirar el Consentimiento de Cookies',
        blocks: [
          { type: 'text', text: 'Tiene todo el derecho a aceptar, rechazar o modificar sus preferencias de cookies en cualquier momento.' },
          { type: 'text', text: 'Modificar la Configuración de Cookies: Haga clic en el enlace de Configuración de Cookies en el pie de página del sitio web para volver a abrir el panel de consentimiento y actualizar sus elecciones.' },
          { type: 'text', text: 'También puede desactivar las cookies a través de la configuración de privacidad integrada de su navegador. Tenga en cuenta que desactivar las cookies de análisis no afectará su uso normal de este sitio web.' }
        ]
      },
      {
        id: 5,
        title: '5. Actualizaciones de Este Aviso',
        blocks: [
          { type: 'text', text: 'Podemos actualizar este Aviso de Cookies ocasionalmente para adaptarnos a los ajustes de funciones del sitio web y los requisitos legales. La última versión se publicará en esta página con una fecha actualizada.' }
        ]
      },
      {
        id: 6,
        title: '6. Contáctenos',
        blocks: [
          { type: 'text', text: 'Si tiene alguna pregunta sobre nuestras reglas de uso de cookies, contáctenos:' }
        ]
      }
    ],
    contact: {
      title: 'Contáctenos',
      name: 'Coronet Energy Technology Co., Ltd',
      address: 'Edificio Guomao Zhigu, Distrito Baohe, Hefei, Anhui, China',
      email: 'info@coronetenergy.com',
      phone: '+86 400-168-7613'
    }
  }
}}

export function useI18n() {
  const t = (key) => {
    const keys = key.split('.')
    let val = messages[currentLocale.value]
    for (const k of keys) {
      val = val?.[k]
      if (val === undefined) return key
    }
    return val
  }

  const locale = computed(() => currentLocale.value)

  const toggleLocale = () => {
    currentLocale.value = currentLocale.value === 'en' ? 'zh' : 'en'
  }

  const setLocale = (code) => {
    if (messages[code]) {
      currentLocale.value = code
    }
  }

  return { t, locale, toggleLocale, setLocale }
}
